import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BASE_URL as API_URL, authFetch } from '../services/api';

// ============================================================
// 🔐 PermissionContext — poore app ka SINGLE source of truth
// ============================================================
// Pehle yahan aur hooks/Usepermissions.js me alag-alag logic tha,
// aur ye provider App.js me mount hi nahi hota tha (Permissionguards
// dead code tha). Ab sab isi se aata hai.
//
//  • Cache-first: localStorage se turant padhta hai (no blank flash)
//  • Background me API se refresh karta hai
//  • ADMIN sab kuch bypass karta hai
// ============================================================

const PermissionContext = createContext(null);

/** localStorage me codes kai shapes me ho sakte hain — sabko normalize karo. */
export const normalizePermissionCodes = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if (typeof raw[0] === 'string') return raw;
  return raw
    .map((p) => p?.code || p?.permission?.code || p?.permissionCode || p?.name)
    .filter(Boolean);
};

/** localStorage se cached codes (provider ke bahar bhi kaam aata hai). */
export const readCachedPermissions = () => {
  try {
    return normalizePermissionCodes(JSON.parse(localStorage.getItem('userPermissions') || '[]'));
  } catch {
    return [];
  }
};

/** Logged-in user ADMIN hai? */
export const readIsAdmin = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const role = String(user?.role || user?.designation || '').toUpperCase();
    return role.includes('ADMIN');
  } catch {
    return false;
  }
};

export const PermissionProvider = ({ children }) => {
  // Cache-first — refresh par "Access Denied" flash nahi hoga
  const [permissions, setPermissions] = useState(readCachedPermissions);
  const [isAdmin, setIsAdmin] = useState(readIsAdmin);
  // Cache mil gaya to loading nahi — background refresh chalta rahega
  const [loading, setLoading] = useState(() => readCachedPermissions().length === 0);

  const loadPermissions = useCallback(async () => {
    const user = (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
    })();

    setIsAdmin(readIsAdmin());

    const designationId = user?.designationId;
    const token = localStorage.getItem('authToken');

    if (!designationId || !token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/designations/${designationId}/permissions`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        const codes = normalizePermissionCodes(data);
        setPermissions(codes);
        localStorage.setItem('userPermissions', JSON.stringify(codes));
        // Sidebar / baaki listeners ko bata do
        window.dispatchEvent(new Event('crm:permissions_updated'));
      }
    } catch (error) {
      console.error('Permission load error:', error);
      // Network fail — cache pe hi chalte raho
      setPermissions(readCachedPermissions());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // localStorage se dobara padho — koi API call nahi (Login.js pehle hi fetch kar chuka hota hai)
  const tokenRef = useRef(localStorage.getItem('authToken'));
  const sync = useCallback(() => {
    tokenRef.current = localStorage.getItem('authToken');
    setPermissions(readCachedPermissions());
    setIsAdmin(readIsAdmin());
    setLoading(false);
  }, []);

  // ⚠️ Provider /login page par hi mount ho jaata hai (tab koi user nahi hota),
  // aur login ke baad SPA navigate hota hai — remount nahi. `storage` event sirf
  // DOOSRE tabs me fire hota hai, isliye usi tab me state purana reh jaata tha
  // (admin ko sidebar me 3 hi item dikhte the jab tak reload na karo).
  //  • crm:auth_changed      → Login.js navigate se pehle fire karta hai
  //  • storage               → doosre tab me login/logout
  //  • crm:permissions_updated → background API refresh
  useEffect(() => {
    window.addEventListener('crm:auth_changed', sync);
    window.addEventListener('storage', sync);
    window.addEventListener('crm:permissions_updated', sync);
    return () => {
      window.removeEventListener('crm:auth_changed', sync);
      window.removeEventListener('storage', sync);
      window.removeEventListener('crm:permissions_updated', sync);
    };
  }, [sync]);

  // Safety net: agar koi login flow event bhejna bhool jaaye, to navigation par
  // token badla hua mile to bhi re-sync ho jaaye.
  const location = useLocation();
  useEffect(() => {
    if (localStorage.getItem('authToken') !== tokenRef.current) sync();
  }, [location.pathname, sync]);

  const value = useMemo(() => {
    // ✅ ADMIN ko sab milta hai
    const has = (code) => isAdmin || permissions.includes(code);
    const hasAny = (codes = []) => isAdmin || codes.some((c) => permissions.includes(c));
    const hasAll = (codes = []) => isAdmin || codes.every((c) => permissions.includes(c));

    return {
      permissions,
      isAdmin,
      loading,
      has,
      hasAny,
      hasAll,
      // legacy naam — purane code se compatibility
      hasPermission: has,
      hasAnyPermission: hasAny,
      hasAllPermissions: hasAll,
      reloadPermissions: loadPermissions,
      clearPermissions: () => {
        setPermissions([]);
        localStorage.removeItem('userPermissions');
      },
    };
  }, [permissions, isAdmin, loading, loadPermissions]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

/**
 * Context se permissions lo.
 * Provider mount na ho to crash nahi karta — localStorage pe fallback karta hai.
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);

  // Fallback — hooks ke rules ke liye hamesha call hona chahiye
  const fallback = useMemo(() => {
    const perms = readCachedPermissions();
    const admin = readIsAdmin();
    const has = (code) => admin || perms.includes(code);
    const hasAny = (codes = []) => admin || codes.some((c) => perms.includes(c));
    const hasAll = (codes = []) => admin || codes.every((c) => perms.includes(c));
    return {
      permissions: perms,
      isAdmin: admin,
      loading: false,
      has,
      hasAny,
      hasAll,
      hasPermission: has,
      hasAnyPermission: hasAny,
      hasAllPermissions: hasAll,
      reloadPermissions: () => {},
      clearPermissions: () => {},
    };
  }, []);

  return context || fallback;
};

export default PermissionContext;
